import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface ExcelViewerProps {
  url: string;
}

export const ExcelViewer = ({ url }: ExcelViewerProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [data, setData] = useState<any[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);

  useEffect(() => {
    const loadExcel = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url);
        if (!response.ok) throw new Error('Error al cargar el archivo');

        const arrayBuffer = await response.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: 'array' });
        
        setWorkbook(wb);
        
        // Seleccionar primera hoja por defecto
        if (wb.SheetNames.length > 0) {
          setSelectedSheet(wb.SheetNames[0]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error cargando Excel:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar el archivo');
        setLoading(false);
      }
    };

    loadExcel();
  }, [url]);

  useEffect(() => {
    if (!workbook || !selectedSheet) return;

    try {
      const worksheet = workbook.Sheets[selectedSheet];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      
      if (jsonData.length === 0) {
        setData([]);
        setHeaders([]);
        return;
      }

      // Primera fila como encabezados
      const headerRow = jsonData[0].map((h: any) => String(h || ''));
      setHeaders(headerRow);

      // Limitar a 100 filas de datos
      const dataRows = jsonData.slice(1, 101);
      setData(dataRows);
    } catch (err) {
      console.error('Error procesando hoja:', err);
      setError('Error al procesar la hoja de cálculo');
    }
  }, [workbook, selectedSheet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!workbook) return null;

  const totalRows = workbook.Sheets[selectedSheet] 
    ? XLSX.utils.sheet_to_json(workbook.Sheets[selectedSheet], { header: 1 }).length - 1 
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-viable-green" />
          <span className="font-medium">Vista previa de Excel</span>
        </div>
        
        {workbook.SheetNames.length > 1 && (
          <Select value={selectedSheet} onValueChange={setSelectedSheet}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Seleccionar hoja" />
            </SelectTrigger>
            <SelectContent>
              {workbook.SheetNames.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {data.length > 0 && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <span>Mostrando {Math.min(100, data.length)} de {totalRows} filas</span>
          {totalRows > 100 && (
            <Badge variant="secondary">
              +{totalRows - 100} filas más
            </Badge>
          )}
        </div>
      )}

      <div className="border rounded-md overflow-auto max-h-[500px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 bg-muted">#</TableHead>
              {headers.map((header, idx) => (
                <TableHead key={idx} className="bg-muted font-semibold">
                  {header || `Columna ${idx + 1}`}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length + 1} className="text-center text-muted-foreground">
                  No hay datos en esta hoja
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  <TableCell className="font-medium text-muted-foreground">{rowIdx + 1}</TableCell>
                  {headers.map((_, colIdx) => (
                    <TableCell key={colIdx}>
                      {row[colIdx] !== undefined && row[colIdx] !== null ? String(row[colIdx]) : ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
